import { Injectable } from '@angular/core';

import { finalize, map, Observable, of } from 'rxjs';

import { IdentityBodyAssessIneligible } from '@reg/shared/model';
import { HttpServiceHelper, IdentityBodyAssessService } from '@reg/shared/service';
import { IJsonApiResponse } from '@reg/shared/json-api/json-api.interface';
import { observableInSeparateThread } from '@reg/shared/helper/observable-helper';

/**
 * Service to get and save bodyAssessIneligible data.
 */
@Injectable()
export class IdentityBodyAssessIneligibleService {
    public static get INCLUDE_INELIGIBILE_REASON(): string {
        return 'ineligibleReason';
    }

    private identityApiBaseUrl: string;
    private cachedMap: { [apiRequest: string]: IdentityBodyAssessIneligible[]; } = {};

    /**
     * Constructor
     */
    constructor(
                private http: HttpServiceHelper,
                private identityBodyAssessService: IdentityBodyAssessService,
    ) {
        this.identityApiBaseUrl = http.configService.getBaseConfig('identityApiBaseUrl');
    }

    /**
     * Get all ineligibles for a given body assessment
     */
    public getIndex(bodyAssessmentId: string, includes: string[] = null): Observable<IdentityBodyAssessIneligible[]> {
        const apiRequestUrl: string = this.getApiUrl(bodyAssessmentId, null, includes, null);

        if (this.cachedMap[apiRequestUrl]) {
            // if cache is available just return it as `Observable`
            return observableInSeparateThread(
                this.cachedMap[apiRequestUrl].map((a: IdentityBodyAssessIneligible) => a.clone()),
            );
        }
        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const bodyAssessIneligibles: IdentityBodyAssessIneligible[] = [];
                const included: Object[] = response.included;

                const bodyData: any = response.data || [];
                for (const bodyAssessIneligible of bodyData) {
                    bodyAssessIneligibles.push(
                        (new IdentityBodyAssessIneligible()).deserialize(bodyAssessIneligible, included),
                    );
                }

                this.cachedMap[apiRequestUrl] = bodyAssessIneligibles;
                return bodyAssessIneligibles.map((i: IdentityBodyAssessIneligible) => i.clone());
            }));
    }

    /**
     * Get a single bodyAssessmentIneligible
     */
    public get(
        identityBodyAssessmentId: string,
        identityBodyAssessIneligibleId: string,
        includes: string[] = null,
    ): Observable<IdentityBodyAssessIneligible> {
        const apiRequestUrl: string = this.getApiUrl(
            identityBodyAssessmentId,
            identityBodyAssessIneligibleId,
            includes,
        );

        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => (new IdentityBodyAssessIneligible().deserialize(response))));
    }

    /**
     * Add or update a bodyAssessmentIneligible
     */
    public save(
        bodyAssessIneligible: IdentityBodyAssessIneligible,
        includes: string[] = null,
    ): Observable<IdentityBodyAssessIneligible> {
        this.http.toastService.clear();

        let response: Observable<IJsonApiResponse>;
        const apiRequestUrl: string = this.getApiUrl(
            bodyAssessIneligible.bodyAssessmentId,
            bodyAssessIneligible.id,
            includes,
        );

        if (bodyAssessIneligible.id) {
            // Update existing BodyAssessIneligible
            response = this.http.patch(apiRequestUrl, bodyAssessIneligible.serialize());
        } else {
            // Add a new BodyAssessIneligible
            response = this.http.post(apiRequestUrl, bodyAssessIneligible.serialize());
        }

        return response
            .pipe(
                map((response: IJsonApiResponse) => (new IdentityBodyAssessIneligible()).deserialize(response)),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Delete the specified ineligibility.
     */
    public delete(bodyAssessmentId: string, bodyAssessmentIneligibilityId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(bodyAssessmentId, bodyAssessmentIneligibilityId, null);

        return this.http.delete(apiRequestUrl)
            .pipe(
                map((response: IJsonApiResponse) => true),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(
        bodyAssessmentId: string,
        bodyAssessIneligibleId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): string {
        let url: string = `${this.identityApiBaseUrl}/body-assessments/${bodyAssessmentId}/ineligibilities/`;

        if (bodyAssessIneligibleId) {
            url += bodyAssessIneligibleId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }

    private clearCache(): void {
        this.cachedMap = {};
        this.identityBodyAssessService.clearCache();
    }
}

